import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button, DropdownSimple } from '@resy/ui-kitchen';
import './CountryCitySelector.scss';
import Chevron from '../../../images/amexrga/chevron-left.svg';

class CountryCitySelector extends React.Component {
  state = {
    allCities: [],
    countries: [],
    cities: [],
    selectedCountry: '',
    selectedCity: '',
    moreCountries: false,
    moreCities: false,
    activeCitiesList: [],
    maximumCountriesShownAtStart: 10,
    maximumCitiesShownAtStart: 10,
    countriesFormatted: [],
    citiesFormatted: [],
  }

  async componentDidMount() {
    await this.setupSelector();
    this.setDropdownOptions();
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillUpdate(cur) {
    const { allCities } = this.state;
    if (cur.allCities.length !== allCities.length) {
      this.setupSelector();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { selectedCountry, moreCountries, moreCities } = this.state;
    if (prevState.selectedCountry !== selectedCountry) {
      this.setDropdownOptions();
    }
    if (prevState.moreCountries !== moreCountries) {
      this.setTabFocus('countries-below-fold');
    }
    if (prevState.moreCities !== moreCities) {
      this.setTabFocus('cities-below-fold');
    }
  }

  setupSelector = () => {
    const { allCities } = this.props;
    const { maximumCountriesShownAtStart } = this.state;

    // proximity is off for now. Setting both proximityCity and proximityCountry as empty
    // const proximityCountry = proximity.countryCode;
    // const proximityCity = proximity.cityCode;
    const proximityCountry = '';
    const proximityCity = '';
    const toSortCities = [];

    const orderByCountry = (country) => {
      let order;
      if (country === 'US') {
        order = 1;
      } else if (country === 'GB') {
        order = 2;
      } else {
        order = 3;
      }
      return order;
    };

    allCities.map((city) => {
      // US first, GB second, the rest after ordered by country name and city name.
      const order = orderByCountry(city.country_code);

      toSortCities.push({
        order,
        countryCode: city.country_code,
        countryName: city.country_name,
        cityCode: city.code,
        cityName: city.name,
      });
      return true;
    });

    const sortedCities = toSortCities
      .filter((location) => location.countryCode && location.countryName) // filter out location without countryCode and countryName
      .sort((current, next) => {
        if (current.countryCode === next.countryCode) {
          return current.cityName > next.cityName ? 1 : -1;
        } else { // eslint-disable-line no-else-return
          if (current.countryCode === 'US') { // eslint-disable-line no-lonely-if
            return -1;
          } else if (next.countryCode === 'US') { // eslint-disable-line
            return 1;
          } else if (current.countryCode === 'GB') { // eslint-disable-line
            return -1;
          } else if (next.countryCode === 'GB') {
            return 1;
          } else {
            return current.countryName > next.countryName ? 1 : -1;
          }
        }
      });

    // Removing duplicates
    const seen = new Set();
    const cities = sortedCities.filter((city) => {
      const duplicate = seen.has(city.cityCode);
      seen.add(city.cityCode);
      return !duplicate;
    });

    // Extract unique countries and create countries array
    const cachedObject = {};
    cities.forEach((country) => {
      cachedObject[country.countryCode] = {
        countryCode: country.countryCode,
        countryName: country.countryName,
        isActive: country.countryCode === proximityCountry,
        isOpen: country.countryCode === proximityCountry,
        isExtended: country.countryCode === proximityCountry,
      };
    });
    const countries = Object.values(cachedObject);
    const idx = countries.findIndex((x) => x.countryCode === proximityCountry);

    this.setState({ moreCountries: idx > maximumCountriesShownAtStart });
    this.setState({
      allCities,
      selectedCountry: proximityCountry,
      countries,
      cities,
      activeCitiesList: cities.filter((city) => city.countryCode === proximityCountry),
    }, () => { this.updateCity(proximityCity); });
  }

  setDropdownOptions = () => {
    const {
      countries,
      activeCitiesList,
    } = this.state;
    const countriesFormatted = countries.map(
      (country) => ({
        label: country.countryName,
        value: country.countryCode,
        key: country.countryCode,
      })
    );

    const citiesFormatted = activeCitiesList.map(
      (country) => ({
        label: country.cityName,
        value: country.cityCode,
        key: country.cityCode,
      })
    );
    this.setState({
      countriesFormatted,
      citiesFormatted,
    });
  }

  updateSelectedCountry = (selectedCountry) => {
    const { countries, cities } = this.state;

    const idx = countries.findIndex((x) => x.countryCode === selectedCountry);
    const tmpCountries = countries.map((item, index) => (
      { ...item, isActive: index === idx }
    ));
    this.setState({
      selectedCountry,
      activeCitiesList: cities.filter((city) => city.countryCode === selectedCountry),
      countries: tmpCountries,
    }, this.updateCity(''));
  }

  updateSelectedCountryFromDropdown = (selectedCountry) => {
    const { countries, cities } = this.state;
    const idx = countries.findIndex((x) => x.countryCode === selectedCountry);

    const updatedCountries = countries.map((country, index) => (
      {
        ...country, isOpen: index === idx, isActive: index === idx, isExtended: false,
      }
    ));

    this.setState({
      selectedCountry,
      activeCitiesList: cities.filter((city) => city.countryCode === selectedCountry),
      countries: updatedCountries,
    });

    this.updateCity('');
  }

  toggleCountryAccordion = (selectedCountry) => {
    const { countries } = this.state;
    const updatedCountries = countries.map((country) => (country.countryCode === selectedCountry ? { ...country, isOpen: !country.isOpen, isExtended: false } : country));

    this.setState({
      countries: updatedCountries,
    });
  }

  extendCities = (countryCode) => {
    const { countries } = this.state;
    const idx = countries.findIndex((x) => x.countryCode === countryCode);
    countries[idx].isExtended = true;
    this.setState({
      countries,
      moreCities: true,
    });
  }

  handleClickExtendedCountries = () => {
    this.setState({
      moreCountries: true,
    });
  }

  setTabFocus = (classParam) => {
    if (document.getElementsByClassName(classParam)[0]) {
      const target = document.getElementsByClassName(classParam)[0].getElementsByTagName('button')[0];
      target?.focus();
    }
  }

  updateCity = (selectedCity) => {
    const { callCity } = this.props;
    const { cities } = this.state;

    const idx = cities.findIndex((x) => x.cityCode === selectedCity);
    let cityCountryText = '';

    if (idx > -1) {
      cityCountryText = `${cities[idx].cityName}, ${cities[idx].countryName}`;
    }
    this.setState({
      selectedCity,
    });
    callCity(selectedCity, cityCountryText);
  }

  handleCityChangeAccordion = (selectedCountry, selectedCity) => {
    this.updateSelectedCountry(selectedCountry);
    this.updateCity(selectedCity);
  }

  handleCountryChangeDropdown = (e) => {
    const { countriesFormatted } = this.state;
    const selectedCountry = e.target.value;
    const selectedCountryName = countriesFormatted
      .find(({ value }) => value === selectedCountry);
    const { handleDirTagging } = this.props;
    this.updateSelectedCountryFromDropdown(selectedCountry);
    this.handleClickExtendedCountries();
    const extraParams = {
      item_name: selectedCountryName.label,
      action_name: 'Country Click',
    };
    handleDirTagging(extraParams);
  }

  handleCityChangeDropdown = (e) => {
    const { citiesFormatted, selectedCountry } = this.state;
    const selectedCity = e.target.value;
    const selectedCityName = citiesFormatted
      .find(({ value }) => value === selectedCity);
    const { handleDirTagging } = this.props;
    this.extendCities(selectedCountry);
    this.updateCity(selectedCity);

    const extraParams = {
      item_name: selectedCityName.label,
      action_name: 'City Click',
    };
    handleDirTagging(extraParams);
  }

  render() {
    const {
      countries,
      selectedCity,
      selectedCountry,
      moreCountries,
      cities,
      maximumCitiesShownAtStart,
      maximumCountriesShownAtStart,
      countriesFormatted,
      citiesFormatted,
    } = this.state;

    const {
      selectorContent, printContent, handleDirTagging,
    } = this.props;
    const {
      extendCountriesText,
      extendCitiesText,
      mobileTitle,
      countryPlaceholder,
      cityPlaceholder,
    } = { ...selectorContent };
    const countryCitySelector = countries.map((country, countryIndex) => {
      const matchingCities = cities.filter((city) => city.countryCode === country.countryCode);
      const cityrows = (
        <div className="child-cities" role="region" id={`cityList${countryIndex}`} aria-labelledby={`countryItem${countryIndex}`} hidden={!country.isOpen}>
          <ul>
            {matchingCities.map((city, cityIndex) => {
              const { cityCode, cityName, countryCode } = city;
              return (
                <li
                  className={cx('city', {
                    'cities-below-fold': cityIndex >= maximumCitiesShownAtStart,
                    active: selectedCity === cityCode,
                  })}
                  key={cityCode}
                >
                  <Button
                    variant="no-style"
                    type="button"
                    className="GDADIR__button-city"
                    data-test-id={`amexgda_directory_page-button-city${cityCode}`}
                    onClick={() => {
                      const extraParams = {
                        item_name: cityName,
                        action_name: 'City Click',
                      };
                      handleDirTagging(extraParams);
                      this.handleCityChangeAccordion(countryCode, cityCode);
                    }}
                  >
                    {cityName}
                  </Button>
                </li>
              );
            })}
          </ul>
          {matchingCities.length > maximumCitiesShownAtStart
            && (
              <Button
                variant="no-style"
                type="button"
                id="btnExtendCities"
                data-test-id="amexgda_directory_page-button-extend-cities"
                onClick={() => this.extendCities(country.countryCode)}
              >
                {printContent(extendCitiesText, 'extendCitiesText')}
              </Button>
            )}
        </div>
      );

      return (
        <div
          className={cx('country', {
            active: country.isActive,
            open: country.isOpen,
            'cities-extended': country.isExtended,
            'countries-below-fold': countryIndex >= maximumCountriesShownAtStart,
            'countries-extended': moreCountries,
          })}
          key={country.countryCode}
        >
          <Button
            variant="no-style"
            type="button"
            id={`countryItem${countryIndex}`}
            data-test-id={`amexgda_directory_page-button-country${country.countryCode}`}
            className="GDADIR__button-country"
            onClick={() => {
              const extraParams = {
                item_name: country.countryName,
                action_name: 'Country Click',
              };
              handleDirTagging(extraParams);
              this.toggleCountryAccordion(country.countryCode);
            }}
            aria-controls={`cityList${countryIndex}`}
            aria-expanded={country.isOpen}
          >
            <Chevron className="GDADIR__chevron" />
            {country.countryName}
          </Button>
          {cityrows}
        </div>
      );
    });

    const selectCountry = (
      <DropdownSimple
        name="country"
        id="countryselect"
        onChange={this.handleCountryChangeDropdown}
        data-test-id="gda_CountryCitySelector-countrySelect"
        options={[
          {
            label: countryPlaceholder,
            value: '',
            key: 'countrySelectorDefaultOption',
          },
          ...countriesFormatted,
        ]}
        value={selectedCountry}
        ariaLabel="Country"
      />
    );

    const selectCity = (
      <DropdownSimple
        name="city"
        id="cityselect"
        onChange={this.handleCityChangeDropdown}
        data-test-id="gda_CountryCitySelector-countrySelect"
        options={[
          {
            label: cityPlaceholder,
            value: '',
            key: 'citySelectorDefaultOption',
          },
          ...citiesFormatted,
        ]}
        value={selectedCity}
        ariaLabel="City"
      />
    );

    return (
      <>
        <div className="GDADIR__country-city-selector">
          <div className="GDADIR__country-city-mobile-selector">
            <h3>{printContent(mobileTitle, 'mobileTitle')}</h3>
            {selectCountry}
            {!countryPlaceholder && printContent(countryPlaceholder, 'countryPlaceholder')}
            {selectCity}
            {!cityPlaceholder && printContent(cityPlaceholder, 'cityPlaceholder')}
          </div>
          <nav className="GDADIR__country-city-desktop-selector" aria-label="Country and city selector">
            {countryCitySelector}
            {countries.length >= maximumCountriesShownAtStart && !moreCountries
              && (
                <Button
                  variant="no-style"
                  type="button"
                  id="btnExtendCountries"
                  data-test-id="amexgda_directory_page-button-extend-countries"
                  onClick={this.handleClickExtendedCountries}
                >
                  {printContent(extendCountriesText, 'extendCountriesText')}
                </Button>
              )}
          </nav>
        </div>
      </>
    );
  }
}

CountryCitySelector.propTypes = {
  allCities: PropTypes.arrayOf(PropTypes.object).isRequired,
  callCity: PropTypes.func.isRequired,
  selectorContent: PropTypes.objectOf(PropTypes.string).isRequired,
  printContent: PropTypes.func.isRequired,
  handleDirTagging: PropTypes.func.isRequired,
};

export default CountryCitySelector;
